import { useSessionState } from '../../shared/hooks/session-state-hook';
import { sessionKey } from '../../shared/util/session-storage';
import PageHeading from '../../shared/components/UIElements/PageHeading';
import EmptyState from '../../shared/components/UIElements/EmptyState';
import Icon from '../../shared/components/UIElements/Icon';
import './Patient.css';
import React, { useContext } from 'react';
import PatientViewList from '../components/PatientViewList';
import Button from '../../shared/components/FormElements/Button';
import ErrorModal from '../../shared/components/UIElements/ErrorModal';
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner';
import { useHttpClient } from '../../shared/hooks/http-hook';
import { AuthContext } from '../../shared/Context/auth-context';
import { BACKEND_URL } from '../../shared/util/backend-url';

const Patient = () => {
  const { isLoading, error, clearError } = useHttpClient();
  const { doctorId, token } = useContext(AuthContext);
  const [searchInput, setSearchInput] = useSessionState(
    sessionKey(`doctor:${doctorId}`, 'search:query'),
    ''
  );
  const [fetchedPatient, setFetchedPatient] = useSessionState(
    sessionKey(`doctor:${doctorId}`, 'search:result'),
    null,
    {
      restore: (value) =>
        value &&
        typeof value.id === 'string' &&
        typeof value.name === 'string' &&
        Array.isArray(value.history)
          ? value
          : null
    }
  );

  const searchHandler = async () => {
    const isMobileNumber = /^\d{10}$/.test(searchInput); // 10-digit number

    const endpoint = isMobileNumber
      ? `${BACKEND_URL}/api/doctor/mobile/${searchInput}`
      : `${BACKEND_URL}/api/doctor/${searchInput}`;

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message);
      }

      setFetchedPatient({
        id: responseData.patientId,
        name: responseData.patientName,
        history: responseData.history || []
      });
    } catch (err) {
      setFetchedPatient(null);
    }
  };

  const resetHandler = () => {
    setSearchInput('');
    setFetchedPatient(null);
    //setError(null);
  };

  return (
    <React.Fragment>
      <ErrorModal error={error} onClear={clearError} />
      {isLoading && <LoadingSpinner asOverlay />}
      <div className="patient-search">
        <PageHeading
          eyebrow="PATIENT CARE"
          title="Find a patient"
          description="Open a patient’s history, create a prescription, or review their reports."
        />
        <section className="search-panel">
          <label htmlFor="patient-search">Patient ID or mobile number</label>
          <div className="search-controls">
            <div className="search-input-wrap">
              <Icon name="search" />
              <input
                id="patient-search"
                type="text"
                placeholder="Enter patient ID or 10-digit mobile number"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') searchHandler();
                }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Button onClick={searchHandler} style={{ margin: '0 0.5rem' }}>
              Search
            </Button>
            <Button onClick={resetHandler} inverse>
              Reset
            </Button>
          </div>
          <p className="search-hint">
            Use the patient’s registered mobile number or their unique Curentia ID.
          </p>
        </section>
        {!fetchedPatient && (
          <div className="search-empty">
            <EmptyState icon="patient" title="Your next consultation starts here">
              Find a patient to view their care history and continue their treatment.
            </EmptyState>
          </div>
        )}
        {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
        {fetchedPatient && (
          <PatientViewList
            items={[
              {
                id: fetchedPatient.id,
                name: fetchedPatient.name,
                history: fetchedPatient.history
              }
            ]}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default Patient;
